import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { useGetHouseholdDataByUserID } from "HouseholdProfile/API/queries";
import {
  useFetchProfileData,
  useGetHouseholdRelationships,
} from "UserProfile/API/queries";

import { useUserID } from "Utilities/urlParamHooks";

import { userProfileKeys } from "./keys";

const useHouseholdData = () => {
  const userID = useUserID();
  const { data } = useGetHouseholdDataByUserID(userID);

  return data || {};
};

const useProfileData = () => {
  const userID = useUserID();

  const { data: profileData } = useFetchProfileData({
    userID,
    enabled: !!userID,
    staleTime: Infinity,
  });

  return profileData || {};
};

const useHouseholdRelationships = () => {
  const userID = useUserID();
  const { data } = useGetHouseholdRelationships({ userID });
  const householdRelationships = data?.householdRelationships || [];
  const usersWithoutRelationships = data?.usersWithoutRelationships || [];

  return [...householdRelationships, ...usersWithoutRelationships];
};

const useClearHouseholdRelationships = () => {
  const userID = useUserID();
  const queryClient = useQueryClient();

  return useCallback(() => {
    if (userID) {
      queryClient.invalidateQueries({
        queryKey: userProfileKeys.householdRelationships(userID),
      });
    }
  }, [queryClient, userID]);
};

const useCanEditFamily = () => {
  const { canEditFamily } = useHouseholdData();
  return canEditFamily;
};

const useIsArchived = () => {
  const { profileUserInfo } = useHouseholdData();
  return profileUserInfo?.isArchived;
};

const useIsUsingOldSendingLogic = () => {
  const { isUsingOldSendingLogic } = useHouseholdData();
  return isUsingOldSendingLogic;
};

const useCanAddHouseholdMembers = () => {
  const { canAddHouseholdMembers } = useHouseholdData();
  return canAddHouseholdMembers;
};

const useCanEditHouseholdTitle = () => {
  const { canEditHouseholdTitle } = useHouseholdData();
  return canEditHouseholdTitle;
};

const useShouldShowProxyContactsSection = () => {
  const isUsingOldSendingLogic = useIsUsingOldSendingLogic();
  const isArchived = useIsArchived();

  return !isUsingOldSendingLogic && !isArchived;
};

const useGetRelationshipsSummaryUser = () => {
  const { userInfo } = useProfileData();

  return {
    ID: userInfo?.userID,
    fname: userInfo?.firstName,
    lname: userInfo?.lastName,
  };
};

export {
  useCanEditFamily,
  useShouldShowProxyContactsSection,
  useProfileData,
  useGetRelationshipsSummaryUser,
  useHouseholdRelationships,
  useCanAddHouseholdMembers,
  useCanEditHouseholdTitle,
  useClearHouseholdRelationships,
};
